import { useState, useEffect, useCallback } from 'react';
import { initAppData, KEYS } from '../services/initService';
import StorageService from '../services/storageService';

import Navbar from '../components/Navbar';
import Hero from '../components/Hero';
import TrustStrip from '../components/TrustStrip';
import MenuSection from '../components/MenuSection';
import SpecialsSection from '../components/SpecialsSection';
import StorySection from '../components/StorySection';
import FeedbackSection from '../components/FeedbackSection';
import Footer from '../components/Footer';
import CartPanel from '../components/CartPanel';
import CartSticky from '../components/CartSticky';

import { ReactComponent as LogoMark } from '../assets/logo.svg';

import '../styles/variables.css';

export default function Home() {
  const [ready, setReady] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        await initAppData();
        const [m, r] = await Promise.all([
          StorageService.getAll(KEYS.MENU),
          StorageService.getAll(KEYS.REVIEWS),
        ]);
        setMenuItems(m.filter((i) => i.active));
        setReviews(r);
      } catch (err) {
        console.error('Failed to initialize app data:', err);
      } finally {
        setReady(true);
      }
    })();
  }, []);

  const handleReviewAdded = useCallback(async () => {
    const r = await StorageService.getAll(KEYS.REVIEWS);
    setReviews(r);
  }, []);

  const addToCart = useCallback((item) => {
    setCart((prev) => {
      const existing = prev.find((c) => c.id === item.id);
      if (existing) return prev.map((c) => (c.id === item.id ? { ...c, qty: c.qty + 1 } : c));
      return [...prev, { ...item, qty: 1 }];
    });
  }, []);

  const updateQty = useCallback((id, qty) => {
    if (qty <= 0) {
      setCart((prev) => prev.filter((c) => c.id !== id));
    } else {
      setCart((prev) => prev.map((c) => (c.id === id ? { ...c, qty } : c)));
    }
  }, []);

  const handleCartClose = useCallback((clearCart = false) => {
    setCartOpen(false);
    if (clearCart) setCart([]);
  }, []);

  if (!ready) {
    return (
      <div className="home-loading-screen">
        <div
          className="home-loading-logo-wrap"
          style={{
            width: 100,
            height: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width={0} height={0} aria-hidden style={{ position: 'absolute', overflow: 'hidden' }}>
            <defs>
              <linearGradient id="homeLoadingGoldGrad" x1="0" y1="0.5" x2="1" y2="0.5">
                <stop offset="0%" stopColor="#BF953F" />
                <stop offset="25%" stopColor="#FCF6BA" />
                <stop offset="50%" stopColor="#B38728" />
                <stop offset="75%" stopColor="#FBF5B7" />
                <stop offset="100%" stopColor="#AA771C" />
              </linearGradient>
            </defs>
          </svg>
          <div className="home-loading-logo-stage">
            <LogoMark
              className="home-loading-logo home-loading-logo--gold"
              style={{ width: '100%', height: '100%', display: 'block', flexShrink: 0 }}
              aria-hidden
            />
          </div>
        </div>
        <style>{`
          .home-loading-screen {
            min-height: 100vh;
            min-height: 100dvh;
            display: grid;
            place-items: center;
            background: var(--dark);
            overflow: hidden;
            perspective: 720px;
          }
          .home-loading-logo--gold path {
            fill: url(#homeLoadingGoldGrad);
          }
          .home-loading-logo-wrap {
            transform-style: preserve-3d;
          }
          .home-loading-logo-stage {
            width: 100%;
            height: 100%;
            animation: homeLoadingPulse 1.55s ease-in-out infinite;
            filter:
              drop-shadow(0 18px 18px rgba(0, 0, 0, 0.34))
              drop-shadow(0 0 20px rgba(252, 246, 186, 0.18));
            transform-style: preserve-3d;
            will-change: transform, opacity, filter;
          }
          .home-loading-logo {
            transform: translateZ(30px);
            filter:
              drop-shadow(0 2px 0 rgba(255, 255, 255, 0.22))
              drop-shadow(0 9px 7px rgba(0, 0, 0, 0.28));
          }
          @keyframes homeLoadingPulse {
            0%, 100% {
              transform: rotateX(14deg) rotateY(-16deg) translateZ(0) scale(0.94);
              opacity: 0.78;
              filter:
                brightness(0.9)
                drop-shadow(0 14px 14px rgba(0, 0, 0, 0.32))
                drop-shadow(0 0 14px rgba(252, 246, 186, 0.12));
            }
            50% {
              transform: rotateX(-8deg) rotateY(14deg) translateZ(28px) scale(1.08);
              opacity: 1;
              filter:
                brightness(1.12)
                drop-shadow(0 24px 22px rgba(0, 0, 0, 0.36))
                drop-shadow(0 0 28px rgba(252, 246, 186, 0.3));
            }
          }
          @media (max-width: 640px) {
            .home-loading-logo-wrap {
              position: fixed;
              top: 50dvh;
              left: 50vw;
              transform: translate(-50%, -50%);
            }
          }
          @media (prefers-reduced-motion: reduce) {
            .home-loading-logo-stage {
              animation: none;
              opacity: 1;
              transform: none;
            }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      <Navbar
        cartCount={cart.reduce((s, i) => s + i.qty, 0)}
        onOpenCart={() => setCartOpen(true)}
      />

      <main>
        <Hero
          onMenu={() => document.getElementById('menu')?.scrollIntoView({ behavior: 'smooth' })}
        />
        <TrustStrip />
        <MenuSection menuItems={menuItems} onAddToCart={addToCart} />
        <SpecialsSection menuItems={menuItems} onAddToCart={addToCart} />
        <StorySection />
        <FeedbackSection reviews={reviews} onReviewAdded={handleReviewAdded} />
      </main>

      <Footer />

      <CartSticky cart={cart} onOpen={() => setCartOpen(true)} />

      <CartPanel
        cart={cart}
        isOpen={cartOpen}
        onClose={handleCartClose}
        onUpdateQty={updateQty}
        onRemove={(id) => setCart((prev) => prev.filter((c) => c.id !== id))}
        onClearOrder={() => setCart([])}
      />
    </>
  );
}
